import { useContext, useEffect, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';

import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import { fetchEntities } from '../../../services/syntiro_honey/data_ingestion.service';
import { EntityLevel, EntityLookupItem } from '../../../types/syntiro_honey';

/** The value the filter carries: an entity id plus the level that id names. */
export interface EntityFilterValue {
  /** The selected entity's id at its own level. */
  entityId: string;
  /** Which org level {@link entityId} names, so the API knows what to match. */
  entityLevel: EntityLevel;
  /** Display name, so the caller can label the active filter without a lookup. */
  name: string;
}

interface EntityFilterSelectProps {
  /** Field label. */
  label: string;
  /** The currently filtered entity id, or null for "all entities". */
  value: string | null;
  /** Display name for `value` while the options load, so the field is never blank. */
  currentLabel?: string | null;
  /** Called with the chosen entity, or null when the filter is cleared. */
  onChange: (entity: EntityFilterValue | null) => void;
}

/** The id that identifies an entity at its own (deepest) level. */
function entityIdOf(entity: EntityLookupItem): string {
  return entity.siteId ?? entity.subdivisionId ?? entity.divisionId ?? entity.companyId;
}

/** A human-readable org level for the option label. */
function levelName(kind: EntityLevel): string {
  switch (kind) {
    case 'SITE':
      return 'Site';
    case 'SUBDIVISION':
      return 'Sub-division';
    case 'DIVISION':
      return 'Division';
    default:
      return 'Company';
  }
}

/**
 * Entity filter for the review list, backed by `GET /entities`. Offers an
 * "All entities" option and reports the level alongside the id, since the
 * API matches a whole subtree and an id alone is ambiguous across the four
 * org-level tables (DECISIONS #36).
 *
 * Always scoped to the logged-in company (no EP-staff exception, unlike
 * LearnedRulesPanel's cross-client picker — this filter lives inside a
 * review session already pinned to one company). `fetchEntities` is called
 * unscoped since Honey's companyId is only recoverable after the first
 * fetch, by matching `client_code` against a returned COMPANY row's
 * `clientCode`.
 * @param {EntityFilterSelectProps} props The current value and change handler.
 * @returns {JSX.Element}
 */
export default function EntityFilterSelect({
  label,
  value,
  currentLabel,
  onChange
}: EntityFilterSelectProps) {
  const [entities, setEntities] = useState<Array<EntityLookupItem>>([]);
  const [entitiesUnavailable, setEntitiesUnavailable] = useState<boolean>(false);
  const [scopeUnresolved, setScopeUnresolved] = useState<boolean>(false);
  const { company } = useContext(CompanyContext);
  const { group } = useContext(GroupContext);
  const loginClientCode = company?.client_code ?? group?.client_code ?? null;

  useEffect(() => {
    let cancelled = false;
    setEntitiesUnavailable(false);
    setScopeUnresolved(false);
    if (loginClientCode === null) {
      setEntities([]);
      setScopeUnresolved(true);
      return undefined;
    }
    fetchEntities()
      .then((items) => {
        if (cancelled) {
          return;
        }
        const matchedCompany = items.find(
          (item) => item.kind === 'COMPANY' && item.clientCode === loginClientCode
        );
        if (matchedCompany === undefined) {
          setEntities([]);
          setScopeUnresolved(true);
          return;
        }
        setEntities(items.filter((item) => item.companyId === matchedCompany.companyId));
      })
      .catch(() => {
        if (!cancelled) {
          setEntitiesUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loginClientCode]);

  const options = entities.map((entity) => ({ entity, id: entityIdOf(entity) }));
  const known = options.some((option) => option.id === value);

  return (
    <TextField
      select
      size="small"
      label={label}
      value={value ?? ''}
      helperText={
        entitiesUnavailable
          ? 'Entity list unavailable — try again shortly'
          : scopeUnresolved
            ? "This company isn't linked to an entity catalogue yet"
            : undefined
      }
      onChange={(event) => {
        const next = options.find((option) => option.id === event.target.value);
        if (next === undefined) {
          onChange(null);
          return;
        }
        onChange({
          entityId: next.id,
          entityLevel: next.entity.kind,
          name: next.entity.name
        });
      }}
    >
      <MenuItem value="">All entities</MenuItem>
      {/* Keeps a filter set before the list loaded visible and clearable. */}
      {value !== null && !known && <MenuItem value={value}>{currentLabel ?? value}</MenuItem>}
      {options.map(({ entity, id }) => (
        <MenuItem key={id} value={id}>
          {entity.name} · {levelName(entity.kind)}
        </MenuItem>
      ))}
    </TextField>
  );
}
